import i18n from 'app-i18n';
import { DateTime, Interval } from 'luxon';
import { v4 as uuidv4 } from 'uuid';
import { red } from '@mui/material/colors';
import { EventInput } from '@fullcalendar/core/index.js';
import {
  DUMMY_EVENT_ID,
  LocationHoursOrderType,
  SetHoursFormDataProps,
  filterAllDayEventsSelectedRange,
} from '../location-hours.utils';

const createEvent = (startDate: string, orderType: string): EventInput => ({
  id: uuidv4(),
  allDay: true,
  title:
    orderType === 'PICKUP_CLOSED'
      ? i18n.t('LocationHours.eventTitles.pickupClosed')
      : i18n.t('LocationHours.eventTitles.deliveryClosed'),
  start: startDate,
  extendedProps: {
    orderType: orderType,
    eventType: 'ClosedDates',
  },
  color: red[500],
});

export const convertSetClosedDatesFormDataToEventSource = (
  data: SetHoursFormDataProps,
): EventInput[] => {
  const { dateRange, pickupClosed, deliveryClosed } = data;

  const events: EventInput[] = [];

  const startDate = DateTime.fromISO(dateRange[0]);
  const endDate = DateTime.fromISO(dateRange[1]);

  const interval = Interval.fromDateTimes(startDate, endDate);

  for (let date of interval.splitBy({ days: 1 })) {
    const currentDate = date.start.toISODate();

    if (pickupClosed) {
      events.push(createEvent(currentDate, 'PICKUP_CLOSED'));
    }

    if (deliveryClosed) {
      events.push(createEvent(currentDate, 'DELIVERY_CLOSED'));
    }
  }

  return events;
};

export const convertClosedDatesToEventInput = (
  hours: string[],
  orderType: LocationHoursOrderType,
): EventInput[] => {
  if (!hours) return [];

  return hours.reduce((acc: EventInput[], curr) => {
    const events = createEvent(curr, orderType);
    return acc.concat(events);
  }, []);
};

export const eventsToClosedDates = (events: EventInput[]) => {
  const result = {
    pickupOffDays: [],
    deliveryOffDays: [],
  };

  events
    .filter(event => event.allDay)
    .forEach(event => {
      if (event.extendedProps.orderType === 'PICKUP_CLOSED') {
        result.pickupOffDays.push(event.start);
      } else if (event.extendedProps.orderType === 'DELIVERY_CLOSED') {
        result.deliveryOffDays.push(event.start);
      }
    });

  return result;
};

export const filterClosedDatesOverlappedEvents = (
  data: SetHoursFormDataProps,
  existingEvents: EventInput[],
): EventInput[] => {
  const { eventId, dateRange } = data;

  const overlappedEvents = filterAllDayEventsSelectedRange(
    existingEvents,
    DateTime.fromISO(dateRange[0]),
    DateTime.fromISO(dateRange[1]),
  );

  // Filter events from eventArray based on matching day of week and order type
  return overlappedEvents.filter(event => {
    return ![eventId, DUMMY_EVENT_ID].includes(event.id);
  });
};
